import type React from "react";
import { memo, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./BpsAdminSidemenuButton.module.scss";
import { ChevronDownIcon } from "../../../assets/icons";
import { MenuItemData } from "../../menu.types";

export type BpsAdminSidemenuButtonProps = {
	item: MenuItemData;
	isOpen: boolean;
	onToggleSubMenu: (text: string, isOpen: boolean) => void;
	executeWrapper?: (func: () => void) => void;
};

/**
 * Side menu entry of the BPS admin portal. Unlike the bills / utility buttons this
 * one always shows its label — the BPS menu has no collapsed icon-only state.
 */
const BpsAdminSidemenuButton: React.FC<BpsAdminSidemenuButtonProps> = memo((props: BpsAdminSidemenuButtonProps) => {
	const { item, isOpen, onToggleSubMenu, executeWrapper } = props;
	const { text, icon, isActiveIcon, route, disabled, subMenuItems } = item;

	const navigator = useNavigate();
	const currentPath = useLocation().pathname;

	const hasSubMenu = !!subMenuItems && subMenuItems.length > 0;

	// A nested page (`/utility-setup/new`, `/utility-setup/:id`) keeps its parent entry selected.
	const isOnRoute = useCallback(
		(itemRoute?: string) => !!itemRoute && (currentPath === itemRoute || currentPath.startsWith(`${itemRoute}/`)),
		[currentPath],
	);

	const isActive = useMemo(() => {
		if (disabled) return false;
		if (isOnRoute(route)) return true;
		return !!subMenuItems?.some(subItem => isOnRoute(subItem.route));
	}, [isOnRoute, route, subMenuItems, disabled]);

	const navigate = useCallback(
		(to?: string) => {
			if (!to) return;
			if (executeWrapper) executeWrapper(() => navigator(to));
			else navigator(to);
		},
		[executeWrapper, navigator],
	);

	const buttonClick = () => {
		if (disabled) return;
		 navigate(route);
	};

	return (
		<div className={disabled ? styles.disabled : ""}>
			<button
				title={text}
				disabled={disabled}
				className={`${styles.sectionButton} ${isActive ? styles.sectionButtonSelected : ""}`}
				onClick={buttonClick}
			>
				{isActive && isActiveIcon ? isActiveIcon : icon}
				<span className={styles.text}>{text}</span>
				{hasSubMenu && <ChevronDownIcon onClick={() => onToggleSubMenu(text, !isOpen)} className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} />}
			</button>
			{hasSubMenu && (
				<div className={`${styles.subMenu} ${isOpen ? styles.subMenuOpen : ""}`}>
					<div>
						{subMenuItems!.map(subItem => (
							<button
								key={subItem.text}
								className={`${styles.subMenuItem} ${isOnRoute(subItem.route) ? styles.subMenuItemSelected : ""}`}
								onClick={() => navigate(subItem.route)}
							>
								{subItem.icon && <span className={styles.subMenuIcon}>{subItem.icon}</span>}
								{subItem.text}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
});

export default BpsAdminSidemenuButton;
